import type { FormikHelpers, FormikProps } from 'formik';
import { Formik } from 'formik';
import type { CSSProperties, ReactNode } from 'react';
import type { z } from 'zod';
import { Divider } from 'src/components/dividers';
import { ErrorHandler } from 'src/components/form/error-handler';
import {
  formGapClasses,
  SimpleFormContainer,
} from 'src/components/form/form-container';
import type { FormModeContextValue } from 'src/components/form/form-mode-context';
import {
  toFormikValidate,
  validFormValues,
} from 'src/components/form/zod-utilities';
import type { FormSchema } from 'src/components/form/zod-utilities';
import { ModalFooter } from 'src/components/overlay/modal';
import { useMeasure } from 'src/hooks/use-measure';
import { useModalContext } from 'src/hooks/use-modal';
import { cn } from 'src/utils/cn';
import type { ConstraintViolation } from 'src/utils/is-constraint-violation';

export type FormProps<Schema extends FormSchema> = {
  enableReinitialize?: boolean;
  initialValues: z.input<Schema>;
  schema: Schema | ((values: z.input<Schema>) => Schema);
  onSubmit: (
    values: z.output<Schema>,
    formikHelpers: FormikHelpers<z.input<Schema>>
  ) => void;
  onReset?: (
    values: z.input<Schema>,
    formikHelpers: FormikHelpers<z.input<Schema>>
  ) => void;
  children: ReactNode | ((props: FormikProps<z.input<Schema>>) => ReactNode);
  /**
   * Form fields might behave slightly differently depending on the `mode` of our form.
   * Currently we have three cases:
   * - regular: Your regular form usually used to submit data.
   * - filter: A form which is used for filtering data.
   * - search: A form which is used for searching data.
   */
  mode?: FormModeContextValue;
  gap?: number;
  constraintViolation: ConstraintViolation | null;
  /**
   * You should only hide the default toast for constraint violations, if it is managed somewhere else.
   * This can happen for example inside wizards, where you want to show a toast
   * _once_, but every step has its own error handler.
   */
  hideToastOnConstraintViolation?: boolean;
  /**
   * If you don't have a cancel or reset button, just pass `null`.
   */
  cancelOrResetButton:
    | ReactNode
    | ((props: FormikProps<z.input<Schema>>) => ReactNode);
  /**
   * Be careful if you pass `null`! It is extremely rare that you don't have a submit button.
   */
  submitButton:
    | ReactNode
    | ((props: FormikProps<z.input<Schema>>) => ReactNode);
  'data-testid'?: string;
  /**
   * If the <Form/> children need to be rendered inside a special container (e.g. for animations)
   * it can be added here. Be careful to not diverge too much from the default behavior.
   */
  container?: ({
    props,
    children,
  }: {
    props: FormikProps<z.input<Schema>>;
    children: ReactNode;
  }) => ReactNode;
};

export function Form<Schema extends FormSchema>({
  mode = 'regular',
  /**
   * In case of filters we enable reinitialization by default.
   * This will update the form on back and forward navigation as
   * the values are derived from query params.
   */
  enableReinitialize = mode === 'filter',
  initialValues,
  schema,
  onSubmit,
  onReset,
  children,
  constraintViolation,
  hideToastOnConstraintViolation,
  cancelOrResetButton,
  submitButton,
  gap,
  'data-testid': testid,
  container = ({ children }) => children,
}: FormProps<Schema>) {
  const [containerRef, containerBounds] = useMeasure();
  const modalContext = useModalContext();
  const hasActions = Boolean(cancelOrResetButton || submitButton);

  // amount of filter columns is dynamic and depends on the width of the container
  const containerWidth = containerBounds.width;
  const filterColumns = Math.floor(containerWidth / 260) || 1; // at least 1 column

  const getActualSchema = (values: z.input<Schema>) => {
    return typeof schema === 'function' ? schema(values) : schema;
  };

  return (
    <Formik
      enableReinitialize={enableReinitialize}
      initialValues={initialValues}
      validate={async (values) => {
        const errors = await toFormikValidate(getActualSchema(values), values);
        // console.log(errors); // uncomment this for easier debugging of validation errors
        return errors;
      }}
      onSubmit={async (values, formikHelpers) =>
        onSubmit(
          await validFormValues(getActualSchema(values), values),
          formikHelpers
        )
      }
      onReset={onReset}
    >
      {(props) => {
        const cancelOrResetAction = (typeof cancelOrResetButton === 'function'
          ? cancelOrResetButton(props)
          : cancelOrResetButton) || <span />;
        const submitAction =
          typeof submitButton === 'function'
            ? submitButton(props)
            : submitButton;
        const actions = (
          <>
            {cancelOrResetAction}
            {submitAction}
          </>
        );

        return container({
          props,
          children: (
            <SimpleFormContainer mode={mode} data-testid={testid}>
              <div
                className={cn(
                  'grid items-center',
                  mode !== 'filter' && formGapClasses
                )}
              >
                <div
                  ref={containerRef}
                  style={
                    mode === 'filter'
                      ? ({
                          '--form-gap':
                            gap !== undefined ? `${gap}rem` : 'normal',
                          // `1fr` is the short form of `minmax(auto, 1fr)`, but using `minmax(0, 1fr)`
                          // prevents form fields from overflowing.
                          '--form-grid-template-columns':
                            'minmax(0, 1fr) '.repeat(filterColumns),
                        } as CSSProperties)
                      : ({
                          '--form-gap':
                            gap !== undefined ? `${gap}rem` : 'normal',
                          '--form-grid-template-columns': 'none',
                        } as CSSProperties)
                  }
                  className={cn(
                    `
                      grid grid-cols-(--form-grid-template-columns) items-center
                      gap-(--form-gap)
                    `,
                    mode === 'filter' && `items-baseline`,
                    gap === undefined && formGapClasses
                  )}
                >
                  {typeof children === 'function' ? children(props) : children}
                </div>

                <ErrorHandler
                  serverError={constraintViolation}
                  hideToastOnServerError={hideToastOnConstraintViolation}
                />

                {mode === 'filter' && <Divider />}

                {hasActions &&
                  (modalContext ? (
                    <ModalFooter>{actions}</ModalFooter>
                  ) : (
                    <div
                      className={`
                        grid grid-flow-col justify-between gap-md
                        legacy:gap-[1rem]
                      `}
                    >
                      {actions}
                    </div>
                  ))}
              </div>
            </SimpleFormContainer>
          ),
        });
      }}
    </Formik>
  );
}
